import { createMemo, createSignal } from "solid-js";
import type { HomeData, HomeNetworkRow, MeResponse } from "./api";
import { moduleRoot } from "./moduleRoot";
import { user } from "./networks";

// UX-4 bucket B / #211 phase 6 — HomePane data signal.
//
// Two consumers:
//   * cold-load — `user()` (the /me resource) carries `home_data` on
//     login. The `homeData` memo projects it out so HomePane reads
//     a single reactive signal rather than reaching into the
//     subject envelope discriminator at every render.
//   * live updates — `userTopic.ts` dispatches the
//     `connection_state_changed` typed event into `patchHomeNetwork`,
//     which mutates the live override signal in-place. The memo prefers
//     the live override when present (last-write-wins) so a stale /me
//     cache cannot overwrite a subsequent broadcast.
//
// Identity-scoped — when `user()` flips (logout → null, login → new
// envelope) the live override resets. Mirror of the same
// "createRoot at module load" pattern used by `networks.ts`,
// `readCursor.ts`, `adminEvents.ts`.
//
// #211 phase 6 (ruling A) — BOTH subjects carry `home_data` now (the
// user + visitor home pages are the SAME data-driven component). The
// only null case is logged-out or a legacy /me predating the field:
//   * `user() === null`             → `null`     (logged out)
//   * `user().home_data` unset      → `null`     (legacy /me; ignore)
//   * `user().home_data` set        → `HomeData` (both subjects)
//
// HomePane renders `HomePaneRegistered` off the non-null result for
// either subject; `null` renders nothing (logged out / loading).

// #717 — `moduleRoot`, not a bare `createRoot`. `homeData` is a module-root memo
// that reads `user()`, so when the boot resource errors it re-throws in a root
// with no error handler and takes the update cycle down with it. This store
// scopes itself by subject id below, so it wants the root and the error
// context, not the identity-reset wiring.
const exports = moduleRoot(() => {
  // Live overrides keyed by slug. Patches from typed events land here;
  // `homeData` overlays them on top of the /me envelope.
  const [overrides, setOverrides] = createSignal<Record<string, HomeNetworkRow>>({});

  // Reset the live override map whenever the subject changes — a fresh
  // /me envelope is authoritative.
  let lastSubjectId: string | null = null;
  const subjectId = (): string | null => {
    const m = user();
    if (!m) return null;
    return `${m.kind}:${m.id}`;
  };

  const homeData = createMemo<HomeData | null>(() => {
    const m: MeResponse | null | undefined = user();
    const sid = subjectId();
    if (sid !== lastSubjectId) {
      lastSubjectId = sid;
      // Drop the override map on subject flip. queueMicrotask defers
      // the set so the memo's own read of `overrides()` returns the
      // pre-flip value for this run; the next run sees the cleared map.
      // (Direct set inside a memo would Solid-warn about cyclic writes.)
      queueMicrotask(() => setOverrides({}));
    }
    if (!m) return null;
    // #211 phase 6 — both subjects carry home_data; no per-kind branch.
    if (!m.home_data) return null;
    const live = overrides();
    if (Object.keys(live).length === 0) return m.home_data;
    // Overlay live patches on the envelope rows by slug. Unknown slugs
    // (broadcast for a network not in the cold-load envelope — e.g.
    // accreted mid-session) are dropped here; the `connection_state_changed`
    // handler also `refetchNetworks()`/refetches /me so a new row lands on
    // the next envelope. `available_networks` rides through unchanged (live
    // patches only touch attached-network rows).
    const merged = m.home_data.networks.map((row) => live[row.slug] ?? row);
    return { networks: merged, available_networks: m.home_data.available_networks };
  });

  const patchHomeNetwork = (row: HomeNetworkRow): void => {
    setOverrides((prev) => ({ ...prev, [row.slug]: row }));
  };

  return { homeData, patchHomeNetwork };
});

export const homeData = exports.homeData;
export const patchHomeNetwork = exports.patchHomeNetwork;
